import { SCHOOL } from "data";
import { useNavigate } from "react-router-dom";
import { useSetPage } from "lib/nav";
import { useResource } from "hooks/useResource";
import { getSettings } from "services/content";

// Short label shown in the social chip, derived from the platform name.
const ABBR = {
  facebook: "f", twitter: "t", x: "x", linkedin: "in", youtube: "yt",
  instagram: "ig", tiktok: "tk", whatsapp: "wa",
};
const abbrFor = (platform = "") =>
  ABBR[platform.trim().toLowerCase()] || platform.trim().slice(0, 2).toLowerCase() || "•";

const COLUMNS = [
  {
    title: "Quick Links",
    links: [
      ["home", "Home"], ["about", "About Us"], ["academics", "Academics"],
      ["admissions", "Admissions"], ["gallery", "Gallery"],
      ["activity", "Activity"], ["contact", "Contact"],
    ],
  },
];

// Default "Programmes" column, used until an admin fills the list in
// admin → Settings → Footer programmes. `href` may be a page id ("academics")
// or a path ("/academics/kcgs", "/departments/fashion-design").
const DEFAULT_PROGRAMMES = [
  { label: "Class VI–X", href: "academics" },
  { label: "HSC – Science", href: "academics" },
  { label: "HSC – Commerce", href: "academics" },
  { label: "HSC – Humanities", href: "academics" },
  { label: "Honours Degree", href: "academics" },
  { label: "All Programmes", href: "academics" },
];

export default function Footer() {
  const setPage = useSetPage();
  const navigate = useNavigate();
  const { data: settings } = useResource(getSettings);
  const socials = (settings?.socials || []).filter((s) => s && s.url);

  // Identity/contact comes from admin → Settings → School Profile, falling back
  // to the static SCHOOL constants until an admin has filled those fields in.
  const p = settings?.profile || {};
  const name = p.name || SCHOOL.name;
  const college = p.college || SCHOOL.college;
  const eiin = p.eiin || SCHOOL.eiin;
  const est = p.established || SCHOOL.est;
  const address = p.address || SCHOOL.address;
  const phone = p.phone || SCHOOL.phone;
  const email = p.email || SCHOOL.email;
  const officeHours = p.officeHours || SCHOOL.officeHours;
  const programmes = (settings?.footerProgrammes || []).filter((l) => (l.label || "").trim());
  const programmeLinks = programmes.length ? programmes : DEFAULT_PROGRAMMES;
  const goProgramme = (href) => {
    if (!href) return setPage("academics");
    if (/^https?:\/\//.test(href)) return window.open(href, "_blank", "noreferrer");
    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setPage(href);
  };
  return (
    <footer className="bg-[#071a11] text-[#8ab8a6] px-10 pt-14 pb-6">
      <div className="max-w-container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr] gap-12 mb-11">
          <div>
            <div className="font-serif text-xl font-bold text-white mb-1">
              {name}
            </div>
            <div className="font-serif text-xl font-bold text-white mb-3.5">
              &amp; {college}
            </div>
            <p className="text-[13px] leading-[1.8] max-w-[260px] mb-[18px]">
              A premier institution for women's education in Khulna since {est}.
              EIIN: {eiin}
            </p>
            {socials.length > 0 && (
              <div className="flex gap-2.5">
                {socials.map((s, i) => (
                  <a
                    key={`${s.platform}-${i}`}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    title={s.platform}
                    aria-label={s.platform}
                    className="w-8 h-8 rounded-lg bg-white/[0.07] border border-white/10 flex items-center justify-center text-[11px] text-[#8ab8a6] cursor-pointer font-bold uppercase no-underline transition-colors hover:text-gold-light hover:border-white/25"
                  >
                    {abbrFor(s.platform)}
                  </a>
                ))}
              </div>
            )}
          </div>

          {COLUMNS.map(({ title, links }) => (
            <div key={title}>
              <div className="text-gold-light font-bold text-xs tracking-[0.08em] uppercase mb-4 pb-2.5 border-b border-white/[0.07]">
                {title}
              </div>
              <div className="flex flex-col gap-2">
                {links.map(([pg, l], i) => (
                  <button
                    key={`${pg}-${i}`}
                    onClick={() => setPage(pg)}
                    className="bg-transparent border-none text-[#8ab8a6] text-[13px] cursor-pointer text-left p-0 font-sans transition-colors hover:text-gold-light"
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Programmes — admin-editable (Settings → Footer programmes) */}
          <div>
            <div className="text-gold-light font-bold text-xs tracking-[0.08em] uppercase mb-4 pb-2.5 border-b border-white/[0.07]">
              Programmes
            </div>
            <div className="flex flex-col gap-2">
              {programmeLinks.map((l, i) => (
                <button
                  key={`${l.label}-${i}`}
                  onClick={() => goProgramme(l.href)}
                  className="bg-transparent border-none text-[#8ab8a6] text-[13px] cursor-pointer text-left p-0 font-sans transition-colors hover:text-gold-light"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-gold-light font-bold text-xs tracking-[0.08em] uppercase mb-4 pb-2.5 border-b border-white/[0.07]">
              Contact
            </div>
            <div className="flex flex-col gap-2.5 text-[13px]">
              <span>📍 {address}</span>
              <span>📞 {phone}</span>
              <span>✉ {email}</span>
              {officeHours && <span>🕒 {officeHours}</span>}
            </div>
          </div>
        </div>

        <div className="border-t border-white/[0.06] pt-5 flex justify-between text-xs flex-wrap gap-2">
          <span>© {new Date().getFullYear()} KCGS &amp; KCC Women's College. All rights reserved.</span>
          <span className="text-white/70">
            Developed by{" "}
            <a
              href="https://datamartbd.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-gold-light hover:underline transition-colors"
            >
              DataMartBD Limited
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
